/*
 * dv-collection-filter.js — client-side spec filter bar for collection pages.
 *
 * Native Dawn facets (Availability, Price, Sort) are metafield-blind unless the
 * merchant enables each one in Search & Discovery. This adds a self-contained
 * chip bar that filters the rendered grid by the specs.* metafields already on
 * every card (data-dvf-* attributes written in main-collection-product-grid).
 *
 * - Facet groups only render when the collection actually has >= 2 distinct
 *   values for them, so the bar adapts per collection with no config.
 * - Selections are OR within a group, AND across groups.
 * - A MutationObserver re-applies filters after native facets/sort AJAX-swap
 *   the grid, and prunes selections whose values no longer exist.
 * - On phones the chips start folded behind the Refine toggle (CSS hides the
 *   body until .is-open); the toggle shows how many values are selected.
 */
(function () {
  'use strict';

  var GRID_SEL = '#product-grid';
  var CONTAINER_SEL = '#ProductGridContainer';

  // Rendered in this order; each maps to a data-dvf-<key> attribute on the card.
  var GROUPS = [
    { key: 'capacity', label: 'Capacity' },
    { key: 'interface', label: 'Interface' },
    { key: 'form', label: 'Form factor' },
    { key: 'memory', label: 'Memory' },
    { key: 'heatsink', label: 'Cooling' },
    { key: 'condition', label: 'Condition' }
  ];

  // Preferred value ordering (fastest / newest first); unknowns fall to the end.
  var ORDER = {
    interface: ['PCIe 5.0', 'PCIe 4.0 / 5.0', 'PCIe 4.0', 'PCIe 3.0', 'SATA III', 'Thunderbolt 3', 'USB4', 'USB 3.2 Gen 2x2', 'USB 3.2 Gen 2', 'USB 3.2 Gen 1', 'USB 3.0', 'UHS-II', 'UHS-I'],
    form: ['M.2 22110', 'M.2 2280', 'M.2 2260', 'M.2 2242', 'M.2 2230', '2.5"', '2.5" Portable', '3.5"', 'Portable', 'Desktop Dock', 'DIMM (288-pin)', 'RDIMM (288-pin)', 'DIMM (240-pin)', 'SO-DIMM (262-pin)', 'SO-DIMM (204-pin)', 'SDXC', 'microSDXC', 'microSDHC'],
    memory: ['DDR5', 'DDR4', 'DDR3'],
    heatsink: ['Heatsink', 'Bare drive'],
    condition: ['New', 'Open box', 'Used']
  };

  function itemValue(item, key) {
    return (item.getAttribute('data-dvf-' + key) || '').trim();
  }

  // "512GB" -> 512, "2TB" -> 2000, "4TB" -> 4000 (for numeric capacity sort).
  function parseCapacity(v) {
    var m = (v || '').match(/([\d.]+)\s*(TB|GB|MB)/i);
    if (!m) return -1;
    var n = parseFloat(m[1]);
    var u = m[2].toUpperCase();
    if (u === 'TB') return n * 1000;
    if (u === 'MB') return n / 1000;
    return n;
  }

  function sortValues(key, arr) {
    if (key === 'capacity') {
      return arr.sort(function (a, b) { return parseCapacity(a) - parseCapacity(b); });
    }
    var ord = ORDER[key];
    if (ord) {
      return arr.sort(function (a, b) {
        var ia = ord.indexOf(a); if (ia === -1) ia = 999;
        var ib = ord.indexOf(b); if (ib === -1) ib = 999;
        return ia !== ib ? ia - ib : a.localeCompare(b);
      });
    }
    return arr.sort();
  }

  function DvFilter(bar) {
    this.bar = bar;
    this.body = bar.querySelector('[data-dv-body]');
    this.countEl = bar.querySelector('[data-dv-count]');
    this.clearEl = bar.querySelector('[data-dv-clear]');
    this.emptyEl = bar.querySelector('[data-dv-empty]');
    this.toggleEl = bar.querySelector('[data-dv-toggle]');
    this.activeEl = bar.querySelector('[data-dv-active]');
    this.scope = bar.closest ? bar.closest('.dv-cfilter-scope') : null;
    this.active = {}; // key -> { value: true }
    this.facets = [];
    this.build();
    this.bind();
    this.apply();
    this.observe();
  }

  DvFilter.prototype.items = function () {
    var grid = document.querySelector(GRID_SEL);
    return grid ? Array.prototype.slice.call(grid.querySelectorAll('.grid__item')) : [];
  };

  DvFilter.prototype.build = function () {
    var items = this.items();
    var facets = [];
    GROUPS.forEach(function (g) {
      var set = {};
      items.forEach(function (it) {
        var v = itemValue(it, g.key);
        if (v) set[v] = true;
      });
      var values = sortValues(g.key, Object.keys(set));
      if (values.length >= 2) facets.push({ key: g.key, label: g.label, values: values });
    });
    this.facets = facets;

    // Drop any active selection whose value no longer exists (e.g. after a
    // native facet narrowed the set) so it can't silently hide everything.
    var present = {};
    facets.forEach(function (f) { present[f.key] = {}; f.values.forEach(function (v) { present[f.key][v] = true; }); });
    for (var k in this.active) {
      for (var v in this.active[k]) {
        if (!present[k] || !present[k][v]) delete this.active[k][v];
      }
    }

    this.bar.hidden = facets.length === 0;

    // The bar carries its own live count; Dawn's static count nodes (desktop
    // + mobile variants) are hidden by CSS while .dv-cfilter-on is set, so
    // the number doesn't print twice. Liquid sets the class for first paint;
    // keep it in step when a native facet swap changes what the bar shows.
    if (this.scope) this.scope.classList.toggle('dv-cfilter-on', !this.bar.hidden);

    // First build: Liquid already rendered these exact chips (same values,
    // same order), so leave them in place instead of re-creating them.
    var ssr = this.body.getAttribute('data-dv-ssr');
    if (ssr !== null) {
      this.body.removeAttribute('data-dv-ssr');
      var total = 0;
      facets.forEach(function (f) { total += f.values.length; });
      if (parseInt(ssr, 10) === total && this.body.querySelectorAll('.dv-cfilter__chip').length === total) return;
    }
    this.render();
  };

  DvFilter.prototype.render = function () {
    var self = this;
    this.body.innerHTML = '';
    this.facets.forEach(function (f) {
      var group = document.createElement('div');
      group.className = 'dv-cfilter__group';
      group.setAttribute('role', 'group');
      group.setAttribute('aria-label', f.label);

      var label = document.createElement('span');
      label.className = 'dv-cfilter__label';
      label.textContent = f.label;
      group.appendChild(label);

      var chips = document.createElement('div');
      chips.className = 'dv-cfilter__chips';
      f.values.forEach(function (v) {
        var chip = document.createElement('button');
        chip.type = 'button';
        chip.className = 'dv-cfilter__chip';
        chip.setAttribute('data-dv-key', f.key);
        chip.setAttribute('data-dv-value', v);
        chip.setAttribute('aria-pressed', self.active[f.key] && self.active[f.key][v] ? 'true' : 'false');
        chip.textContent = v;
        chips.appendChild(chip);
      });
      group.appendChild(chips);
      self.body.appendChild(group);
    });
  };

  DvFilter.prototype.bind = function () {
    var self = this;
    this.body.addEventListener('click', function (e) {
      var chip = e.target.closest ? e.target.closest('.dv-cfilter__chip') : null;
      if (!chip) return;
      var key = chip.getAttribute('data-dv-key');
      var val = chip.getAttribute('data-dv-value');
      if (!self.active[key]) self.active[key] = {};
      if (self.active[key][val]) {
        delete self.active[key][val];
        chip.setAttribute('aria-pressed', 'false');
      } else {
        self.active[key][val] = true;
        chip.setAttribute('aria-pressed', 'true');
      }
      self.apply();
    });
    if (this.clearEl) this.clearEl.addEventListener('click', function () { self.clear(); });
    if (this.toggleEl) {
      this.toggleEl.addEventListener('click', function () {
        var open = !self.bar.classList.contains('is-open');
        self.bar.classList.toggle('is-open', open);
        self.toggleEl.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    }
    if (this.emptyEl) {
      var eb = this.emptyEl.querySelector('[data-dv-empty-clear]');
      if (eb) eb.addEventListener('click', function () { self.clear(); });
    }
  };

  DvFilter.prototype.activeKeys = function () {
    var keys = [];
    for (var k in this.active) { if (Object.keys(this.active[k]).length) keys.push(k); }
    return keys;
  };

  // Does item satisfy every active group except `ignoreKey`? (OR within group.)
  DvFilter.prototype.matches = function (item, ignoreKey) {
    var keys = this.activeKeys();
    for (var i = 0; i < keys.length; i++) {
      var k = keys[i];
      if (k === ignoreKey) continue;
      if (!this.active[k][itemValue(item, k)]) return false;
    }
    return true;
  };

  DvFilter.prototype.apply = function () {
    var self = this;
    var items = this.items();
    var visible = 0;
    items.forEach(function (it) {
      var show = self.matches(it, null);
      it.classList.toggle('dvf-hidden', !show);
      if (show) visible++;
    });
    var activeCount = this.activeKeys().length;
    if (this.countEl) this.countEl.textContent = this.countLabel(visible, items.length, activeCount);
    if (this.clearEl) this.clearEl.hidden = activeCount === 0;
    if (this.activeEl) {
      var selected = 0;
      for (var k in this.active) selected += Object.keys(this.active[k]).length;
      this.activeEl.textContent = selected ? String(selected) : '';
      this.activeEl.hidden = selected === 0;
      if (this.toggleEl) {
        this.toggleEl.setAttribute('aria-label', selected ? 'Refine, ' + selected + ' selected' : 'Refine');
      }
    }
    if (this.emptyEl) this.emptyEl.hidden = visible !== 0;
    this.updateChipStates(items);
  };

  // The grid only holds one page of cards, so `onPage` is never the collection
  // total once Dawn paginates. Unfiltered, report the collection total (what
  // the pagination control implies); filtered, say plainly that the chips only
  // reached this page, so the number can't be read as a collection-wide count.
  DvFilter.prototype.countLabel = function (visible, onPage, activeCount) {
    var total = parseInt(this.countEl.getAttribute('data-dv-total'), 10);
    if (!total || isNaN(total)) total = onPage;
    if (activeCount === 0) return total + (total === 1 ? ' product' : ' products');
    if (total > onPage) return visible + ' of ' + onPage + ' on this page';
    return visible + (visible === 1 ? ' product' : ' products');
  };

  // Dim chips that would return zero results given the other active groups.
  DvFilter.prototype.updateChipStates = function (items) {
    var self = this;
    var chips = this.body.querySelectorAll('.dv-cfilter__chip');
    Array.prototype.forEach.call(chips, function (chip) {
      if (chip.getAttribute('aria-pressed') === 'true') { chip.classList.remove('is-empty'); return; }
      var key = chip.getAttribute('data-dv-key');
      var val = chip.getAttribute('data-dv-value');
      var n = 0;
      for (var i = 0; i < items.length; i++) {
        if (self.matches(items[i], key) && itemValue(items[i], key) === val) { n++; break; }
      }
      chip.classList.toggle('is-empty', n === 0);
    });
  };

  DvFilter.prototype.clear = function () {
    this.active = {};
    var chips = this.body.querySelectorAll('.dv-cfilter__chip[aria-pressed="true"]');
    Array.prototype.forEach.call(chips, function (c) { c.setAttribute('aria-pressed', 'false'); });
    this.apply();
  };

  DvFilter.prototype.observe = function () {
    var self = this;
    var container = document.querySelector(CONTAINER_SEL);
    if (!container || !window.MutationObserver) return;
    var t;
    this._obs = new MutationObserver(function () {
      clearTimeout(t);
      t = setTimeout(function () { self.build(); self.apply(); }, 40);
    });
    this._obs.observe(container, { childList: true, subtree: true });
  };

  function init() {
    var bars = document.querySelectorAll('[data-dv-cfilter]');
    Array.prototype.forEach.call(bars, function (bar) {
      if (bar._dvInit) return;
      bar._dvInit = true;
      new DvFilter(bar);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
